'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { isNil } from 'es-toolkit';

import type { BreadcrumbSegment } from '@savvy-kit/templates/page-title';

type PageBreadcrumbContextValue = {
  readonly pageBreadcrumbs: readonly BreadcrumbSegment[];
  readonly setPageBreadcrumbs: (crumbs: BreadcrumbSegment[]) => void;
  readonly pageTitle: string | null;
  readonly setPageTitle: (title: string | null) => void;
  readonly pageTitleSuffix: ReactNode;
  readonly setPageTitleSuffix: (suffix: ReactNode) => void;
  /**
   * True when a sub-page is fetching its own title. Layout shows a skeleton
   * h1 + last-segment breadcrumb so the title doesn't flash from a fallback
   * (household / tab name) to the real value.
   */
  readonly pageTitleLoading: boolean;
  readonly setPageTitleLoading: (loading: boolean) => void;
  /**
   * When true, the layout suppresses the active sidebar-tab label from the
   * breadcrumb (e.g. "Service Calendar"). Use when the page's own title or
   * heading already names the section and the breadcrumb leaf would just
   * repeat it. Resets on pathname change like other fields here.
   */
  readonly hideActiveTabLabel: boolean;
  readonly setHideActiveTabLabel: (hide: boolean) => void;
};

const noop = () => {
  // intentionally empty default
};

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue>({
  pageBreadcrumbs: [],
  setPageBreadcrumbs: noop,
  pageTitle: null,
  setPageTitle: noop,
  pageTitleSuffix: null,
  setPageTitleSuffix: noop,
  pageTitleLoading: false,
  setPageTitleLoading: noop,
  hideActiveTabLabel: false,
  setHideActiveTabLabel: noop,
});

export function PageBreadcrumbProvider({ children }: { readonly children: ReactNode }) {
  // Raw setters are wrapped below to expose a stable, side-effect-aware API
  // through the context (e.g. `setPageTitle` also clears `pageTitleLoading`).
  // The `*State` suffix marks the internal setter so the public API can keep
  // the natural `setPageTitle` name.
  /* eslint-disable react/hook-use-state -- raw setters intentionally suffixed; wrappers below expose the public API */
  const [pageBreadcrumbs, setPageBreadcrumbsState] = useState<BreadcrumbSegment[]>([]);
  const [pageTitle, setPageTitleState] = useState<string | null>(null);
  const [pageTitleSuffix, setPageTitleSuffixState] = useState<ReactNode>(null);
  const [pageTitleLoading, setPageTitleLoadingState] = useState(false);
  const [hideActiveTabLabel, setHideActiveTabLabelState] = useState(false);
  /* eslint-enable react/hook-use-state */
  const pathname = usePathname();

  // Clear page-level breadcrumbs and title on navigation so stale values don't flash
  useEffect(() => {
    setPageBreadcrumbsState([]);
    setPageTitleState(null);
    setPageTitleSuffixState(null);
    setPageTitleLoadingState(false);
    setHideActiveTabLabelState(false);
  }, [pathname]);

  const setPageBreadcrumbs = useCallback((crumbs: BreadcrumbSegment[]) => {
    setPageBreadcrumbsState(crumbs);
  }, []);

  const setPageTitle = useCallback((title: string | null) => {
    setPageTitleState(title);
    if (!isNil(title)) {
      setPageTitleLoadingState(false);
    }
  }, []);

  const setPageTitleSuffix = useCallback((suffix: ReactNode) => {
    setPageTitleSuffixState(suffix);
  }, []);

  const setPageTitleLoading = useCallback((loading: boolean) => {
    setPageTitleLoadingState(loading);
  }, []);

  const setHideActiveTabLabel = useCallback((hide: boolean) => {
    setHideActiveTabLabelState(hide);
  }, []);

  const value = useMemo(
    () => ({
      pageBreadcrumbs,
      setPageBreadcrumbs,
      pageTitle,
      setPageTitle,
      pageTitleSuffix,
      setPageTitleSuffix,
      pageTitleLoading,
      setPageTitleLoading,
      hideActiveTabLabel,
      setHideActiveTabLabel,
    }),
    [
      pageBreadcrumbs,
      setPageBreadcrumbs,
      pageTitle,
      setPageTitle,
      pageTitleSuffix,
      setPageTitleSuffix,
      pageTitleLoading,
      setPageTitleLoading,
      hideActiveTabLabel,
      setHideActiveTabLabel,
    ],
  );

  return <PageBreadcrumbContext.Provider value={value}>{children}</PageBreadcrumbContext.Provider>;
}

export function usePageBreadcrumbs() {
  return useContext(PageBreadcrumbContext);
}
